import React, { useEffect, useState } from "react";
import { Settings, X, RefreshCw, AlertCircle } from "lucide-react";

function AdminUpdates() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Quantity Update States
  const [selectedRoomIdQty, setSelectedRoomIdQty] = useState("");
  const [newQuantity, setNewQuantity] = useState("");
  const [qtyLoading, setQtyLoading] = useState(false);
  const [qtyMessage, setQtyMessage] = useState({ text: "", type: "" });

  // Price Update States
  const [selectedRoomIdPrice, setSelectedRoomIdPrice] = useState("");
  const [priceType, setPriceType] = useState("single");
  const [newPrice, setNewPrice] = useState("");
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceMessage, setPriceMessage] = useState({ text: "", type: "" });

  const fetchRooms = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("https://karunaregent.com/karuna_api/get_room_details");
      if (!response.ok) throw new Error("Failed to load rooms");

      const result = await response.json();
      if (result.status_code !== "200" || !result.details) {
        throw new Error("Invalid data received");
      }

      const sorted = result.details.sort((a, b) => a.id - b.id);
      setRooms(sorted);

      if (sorted.length > 0) {
        setSelectedRoomIdQty(sorted[0].id);
        setSelectedRoomIdPrice(sorted[0].id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchRooms();
    }
  }, [isModalOpen]);

  const handleUpdateQuantity = async (e) => {
    e.preventDefault();
    if (!newQuantity || newQuantity < 0) {
      setQtyMessage({ text: "Enter a valid quantity", type: "error" });
      return;
    }

    setQtyLoading(true);
    setQtyMessage({ text: "", type: "" });

    try {
      const res = await fetch("https://karunaregent.com/karuna_api/update_room_quantity", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedRoomIdQty, counts: newQuantity }),
      });

      const data = await res.json();
      if (data.status_code === "200") {
        setQtyMessage({ text: "Quantity updated!", type: "success" });
        setNewQuantity("");
        fetchRooms();
      } else {
        throw new Error("Update failed");
      }
    } catch (err) {
      setQtyMessage({ text: "Failed to update", type: "error" });
    } finally {
      setQtyLoading(false);
    }
  };

  const handleUpdatePrice = async (e) => {
    e.preventDefault();
    if (!newPrice || newPrice < 0) {
      setPriceMessage({ text: "Enter a valid price", type: "error" });
      return;
    }

    setPriceLoading(true);
    setPriceMessage({ text: "", type: "" });

    try {
      const res = await fetch("https://karunaregent.com/karuna_api/update_room_price", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedRoomIdPrice,
          price: newPrice,
          price_type: priceType,
        }),
      });

      const data = await res.json();
      if (data.status_code === "200") {
        setPriceMessage({ text: "Price updated!", type: "success" });
        setNewPrice("");
        fetchRooms();
      } else {
        throw new Error("Update failed");
      }
    } catch (err) {
      setPriceMessage({ text: "Failed to update", type: "error" });
    } finally {
      setPriceLoading(false);
    }
  };

  return (
    <>
      {/* Full screen background, centered button with normal size */}
      <div className="bg-gray-50 flex items-center justify-end p-6">
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-3 px-10 py-5 bg-blue-600 text-white text-xl font-semibold rounded-xl hover:bg-blue-700 active:scale-95 transition shadow-lg"
        >
          <Settings className="w-7 h-7" />
          Room Updates
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-8 py-6 flex justify-between items-center">
              <h2 className="text-3xl font-bold text-gray-900">Room Management</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={fetchRooms}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="p-8">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
                  <p className="mt-4 text-gray-600">Loading rooms...</p>
                </div>
              ) : (
                <>
                  {/* Forms */}
                  <div className="grid md:grid-cols-2 gap-8 mb-10">
                    {/* Quantity Update */}
                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                      <h3 className="text-2xl font-semibold mb-5">Update Room Quantity</h3>
                      <form onSubmit={handleUpdateQuantity} className="space-y-4">
                        <select
                          value={selectedRoomIdQty}
                          onChange={(e) => setSelectedRoomIdQty(e.target.value)}
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Room</option>
                          {rooms.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.room_name} (Current: {r.room_quantity})
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="0"
                          value={newQuantity}
                          onChange={(e) => setNewQuantity(e.target.value)}
                          placeholder="New quantity"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <button
                          type="submit"
                          disabled={qtyLoading}
                          className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-70 transition"
                        >
                          {qtyLoading ? "Updating..." : "Update Quantity"}
                        </button>
                        {qtyMessage.text && (
                          <p className={`text-center font-medium ${qtyMessage.type === "success" ? "text-green-600" : "text-red-600"}`}>
                            {qtyMessage.text}
                          </p>
                        )}
                      </form>
                    </div>

                    {/* Price Update */}
                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                      <h3 className="text-2xl font-semibold mb-5">Update Room Price</h3>
                      <form onSubmit={handleUpdatePrice} className="space-y-4">
                        <select
                          value={selectedRoomIdPrice}
                          onChange={(e) => setSelectedRoomIdPrice(e.target.value)}
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                          required
                        >
                          <option value="">Select Room</option>
                          {rooms.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.room_name}
                            </option>
                          ))}
                        </select>

                        <div className="flex gap-8 justify-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="single"
                              checked={priceType === "single"}
                              onChange={(e) => setPriceType(e.target.value)}
                              className="text-green-600"
                            />
                            <span>Single</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="double"
                              checked={priceType === "double"}
                              onChange={(e) => setPriceType(e.target.value)}
                              className="text-green-600"
                            />
                            <span>Double</span>
                          </label>
                        </div>

                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={newPrice}
                          onChange={(e) => setNewPrice(e.target.value)}
                          placeholder="New price (₹)"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                          required
                        />
                        <button
                          type="submit"
                          disabled={priceLoading}
                          className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-70 transition"
                        >
                          {priceLoading ? "Updating..." : "Update Price"}
                        </button>
                        {priceMessage.text && (
                          <p className={`text-center font-medium ${priceMessage.type === "success" ? "text-green-600" : "text-red-600"}`}>
                            {priceMessage.text}
                          </p>
                        )}
                      </form>
                    </div>
                  </div>

                  {/* Current Rooms Table */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-2xl font-semibold mb-5">Current Room Details</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-200">
                          <tr>
                            <th className="px-6 py-3 font-medium">Room Name</th>
                            <th className="px-6 py-3 font-medium">Quantity</th>
                            <th className="px-6 py-3 font-medium">Single Price</th>
                            <th className="px-6 py-3 font-medium">Double Price</th>
                            <th className="px-6 py-3 font-medium">Size</th>
                            <th className="px-6 py-3 font-medium">Facilities</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-300">
                          {rooms.map((room) => (
                            <tr key={room.id} className="hover:bg-gray-100 transition">
                              <td className="px-6 py-4 font-medium">{room.room_name}</td>
                              <td className="px-6 py-4 text-center font-semibold text-blue-600">
                                {room.room_quantity}
                              </td>
                              <td className="px-6 py-4">₹{room.single_price}</td>
                              <td className="px-6 py-4">₹{room.double_price}</td>
                              <td className="px-6 py-4">{room.room_size} sq ft</td>
                              <td className="px-6 py-4 text-sm text-gray-600">{room.room_details}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminUpdates;